import { useEffect, useRef } from 'react';
import { subscribe, type SubscriptionHandlers } from './subscription';

/**
 * Keeps one realtime subscription (ADR-028 §6) alive while `planId` is open. The version and
 * the handlers are read through refs, so re-renders never reconnect — only opening a
 * different plan (or closing it) does.
 */
export function useRealtime(
  planId: string | null,
  version: number | null,
  handlers: SubscriptionHandlers,
  subscribeFn: typeof subscribe = subscribe,
): void {
  const versionRef = useRef(version);
  const handlersRef = useRef(handlers);
  versionRef.current = version;
  handlersRef.current = handlers;

  useEffect(() => {
    if (planId === null) return undefined;
    return subscribeFn(planId, () => versionRef.current ?? -1, {
      onPlan: (plan) => handlersRef.current.onPlan(plan),
      onRenamed: (renamedPlanId, name) => handlersRef.current.onRenamed(renamedPlanId, name),
    });
  }, [planId, subscribeFn]);
}
